/*
 * Code based on React Admin FileInput component.
 */
import {
    Children,
    isValidElement,
    useState,
} from 'react';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useDropzone } from 'react-dropzone';
import FormHelperText from '@mui/material/FormHelperText';
import LinearProgress from '@mui/material/LinearProgress';
import {
    useInput,
    useTranslate,
    shallowEqual,
    RecordContextProvider,
    useDataProvider,
} from 'ra-core';
import { InputHelperText, Labeled, sanitizeInputRestProps } from 'react-admin';
import { CustomFileInputPreview } from './CustomFileInputPreview';

export const CustomFileInput = (props) => {
    const {
        accept,
        children,
        className,
        format,
        helperText,
        inputProps: inputPropsOptions,
        maxSize,
        minSize,
        multiple = false,
        label,
        labelMultiple = 'ra.input.file.upload_several',
        labelSingle = 'ra.input.file.upload_single',
        options = {},
        onRemove: onRemoveProp,
        parse,
        placeholder,
        resource,
        source,
        validate,
        validateFileRemoval,
        filetype,
        ...rest
    } = props;
    const { onDrop: onDropProp } = options;
    const translate = useTranslate();

    const dataProvider = useDataProvider();
    const [loading, setLoading] = useState(false);

    // turn a browser dropped file structure into expected structure
    const transformFile = file => {
        if (!(file instanceof File)) {
            return file;
        }

        const preview = URL.createObjectURL(file);
        const transformedFile = {
            rawFile: file,
            src: preview,
            title: file.name,
        };

        return transformedFile;
    };

    const transformFiles = (files) => {
        if (!files) {
            return multiple ? [] : null;
        }

        if (Array.isArray(files)) {
            return files.map(transformFile);
        }

        return transformFile(files);
    };

    const validationArray = validate == null
        ? []
        : Array.isArray(validate) ? validate : [validate];

    if (loading) {
        validationArray.unshift(() => 'Please wait until the file is uploaded.');
    }

    const {
        id,
        field: { onChange, value },
        fieldState,
        formState: { isSubmitted },
        isRequired,
    } = useInput({
        format: format || transformFiles,
        parse: parse || transformFiles,
        source,
        validate: validationArray,
        ...rest,
    });

    const onFileChange = (files) => {
        if (files == null) {
            onChange(files);
            return;
        }

        const filesToUpload = Array.isArray(files)
            ? files.filter((f) => f instanceof File)
            : [files];

        if (filesToUpload.length === 0) {
            onChange(files);
            return;
        }

        setLoading(true);

        const upload = dataProvider.uploadFiles(filetype, filesToUpload);

        upload.then((result) => {
            if (multiple) {
                const allFiles = [
                    ...result.data,
                    ...files.filter((f) => !(f instanceof File))
                ];

                onChange(allFiles);
            } else {
                const [uploadedFile] = result.data;
                onChange(uploadedFile);
            }
        });

        upload.catch(() => {
            alert('Upload failed. Please try again.')
        });

        upload.finally(() => {
            setLoading(false);
        });
    };

    const { isTouched, error, invalid } = fieldState;
    const files = value ? (Array.isArray(value) ? value : [value]) : [];

    const onDrop = (newFiles, rejectedFiles, event) => {
        const updatedFiles = multiple ? [...files, ...newFiles] : [...newFiles];

        if (updatedFiles.length > 0) {
            if (multiple) {
                onFileChange(updatedFiles);
            } else {
                onFileChange(updatedFiles[0]);
            }
        }

        if (onDropProp) {
            onDropProp(newFiles, rejectedFiles, event);
        }

        if (rejectedFiles?.length > 0) {
            let errorMessage = 'Files must be';

            if (accept != null) {
                errorMessage += ` of type ${accept}`;
            }

            if (maxSize != null && maxSize < Infinity) {
                errorMessage += ` with a size of max. ${maxSize / 1e6}MB`;
            }

            errorMessage += '.\n\nThe following files are not acceptable:\n';
            errorMessage += rejectedFiles.map(({ file }) => file.name).join('\n');

            alert(errorMessage);
        }
    };

    const onRemove = file => async () => {
        if (validateFileRemoval) {
            try {
                await validateFileRemoval(file);
            } catch (e) {
                return;
            }
        }
        if (multiple) {
            const filteredFiles = files.filter(
                stateFile => !shallowEqual(stateFile, file)
            );
            onChange(filteredFiles);
        } else {
            onChange(null);
        }

        if (onRemoveProp) {
            onRemoveProp(file);
        }
    };

    const childrenElement =
        children && isValidElement(Children.only(children))
            ? (Children.only(children))
            : undefined;

    const { getRootProps, getInputProps } = useDropzone({
        accept,
        maxSize,
        minSize,
        multiple,
        ...options,
        onDrop,
    });

    const dropZone = loading
        ? (
            <div className={FileInputClasses.dropZone}>
                <div style={{ margin: '2em 30%' }}>
                    <LinearProgress />
                </div>
            </div>
        )
        : (
            <div
                {...getRootProps({
                    className: FileInputClasses.dropZone,
                    'data-testid': 'dropzone',
                })}
            >
                <input
                    id={id}
                    name={id}
                    {...getInputProps({
                        ...inputPropsOptions,
                    })}
                />
                {placeholder ? (
                    placeholder
                ) : multiple ? (
                    <p>{translate(labelMultiple)}</p>
                ) : (
                    <p>{translate(labelSingle)}</p>
                )}
            </div>
        );

    return (
        <StyledLabeled
            htmlFor={id}
            label={label}
            className={clsx('ra-input', `ra-input-${source}`, className)}
            source={source}
            resource={resource}
            isRequired={isRequired}
            color={(isTouched || isSubmitted) && invalid && 'error'}
            {...sanitizeInputRestProps(rest)}
        >
            <>
                {dropZone}
                <FormHelperText error={(isTouched || isSubmitted) && invalid}>
                    <InputHelperText
                        touched={isTouched || isSubmitted}
                        error={error?.message}
                        helperText={helperText}
                    />
                </FormHelperText>
                {children && (
                    <div className="previews">
                        {files.map((file, index) => (
                            <CustomFileInputPreview
                                key={index}
                                file={file}
                                filetype={filetype}
                                onRemove={onRemove(file)}
                                className={FileInputClasses.removeButton}
                            >
                                <RecordContextProvider value={file}>
                                    {childrenElement}
                                </RecordContextProvider>
                            </CustomFileInputPreview>
                        ))}
                    </div>
                )}
            </>
        </StyledLabeled>
    );
};

CustomFileInput.propTypes = {
    accept: PropTypes.string,
    children: PropTypes.element,
    className: PropTypes.string,
    id: PropTypes.string,
    isRequired: PropTypes.bool,
    label: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
        PropTypes.element,
    ]),
    labelMultiple: PropTypes.string,
    labelSingle: PropTypes.string,
    maxSize: PropTypes.number,
    minSize: PropTypes.number,
    multiple: PropTypes.bool,
    validateFileRemoval: PropTypes.func,
    options: PropTypes.object,
    resource: PropTypes.string,
    source: PropTypes.string,
    placeholder: PropTypes.node,
    format: PropTypes.any,
    helperText: PropTypes.any,
    inputProps: PropTypes.any,
    onRemove: PropTypes.any,
    parse: PropTypes.any,
    validate: PropTypes.any,
    filetype: PropTypes.string
};

const PREFIX = 'CustomRaFileInput';

export const FileInputClasses = {
    dropZone: `${PREFIX}-dropZone`,
    removeButton: `${PREFIX}-removeButton`,
};

const StyledLabeled = styled(Labeled, {
    name: PREFIX,
    overridesResolver: (props, styles) => styles.root,
})(({ theme }) => ({
    width: '100%',
    [`& .${FileInputClasses.dropZone}`]: {
        background: theme.palette.background.default,
        borderRadius: theme.shape.borderRadius,
        fontFamily: theme.typography.fontFamily,
        cursor: 'pointer',
        padding: theme.spacing(1),
        textAlign: 'center',
        color: theme.palette.getContrastText(theme.palette.background.default),
    },
    [`& .${FileInputClasses.removeButton}`]: {},
}));
